import { hashKey, type QueryKey } from "@tanstack/react-query";
import {
  contains,
  findRecord,
  isObject,
  pagesOf,
  patchPages,
  removeFromPages,
  withPages,
  type CacheEntry,
  type CacheSlot,
  type Merge,
  type PageShape,
  type RecordChange,
} from "./cacheSlots";

export type Filters = Readonly<Record<string, unknown>>;

export interface SortTerm {
  key: string;
  direction: "asc" | "desc";
}

/** A list query read back from its key: what it filters by and how it is sorted. */
export interface ListView {
  filters: Filters;
  sort: readonly SortTerm[];
}

export type Compare<R> = (a: R, b: R) => number;

export function sortTerms(value: unknown): SortTerm[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (term): term is SortTerm =>
      isObject(term) &&
      typeof term.key === "string" &&
      (term.direction === "asc" || term.direction === "desc"),
  );
}

/**
 * The backend's list rules an entity replicates, shared by its lists and its
 * aggregates. `accepts` covers every filter over a field a write can change and
 * answers undefined when the cache cannot tell; `narrowing` lists the filters
 * that only ever take rows away, so a list holding the record under them also
 * proves it belongs to the wider set; `runs` reads the value of a text sort key
 * the backend keeps contiguous; `opaque` projects what a filter the client
 * cannot evaluate reads; `order` ranks rows by each non-text sort key;
 * `tieBreak` is the backend's last term.
 */
export interface ViewRules<R> {
  accepts: Readonly<Record<string, (value: unknown, row: R) => boolean | undefined>>;
  narrowing?: readonly string[];
  runs?: Readonly<Record<string, (row: R) => unknown>>;
  opaque?: Readonly<Record<string, (row: R) => unknown>>;
  order?: Readonly<Record<string, (row: R) => number | null>>;
  tieBreak: Compare<R>;
}

export type ViewShape<P, R, TId> = PageShape<P, R, TId> & { total: (page: P) => number };

export function isActive(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return false;
  return !Array.isArray(value) || value.length > 0;
}

function normalized(filters: Filters, omit: (key: string) => boolean): Filters {
  return Object.fromEntries(
    Object.entries(filters).filter(([key, value]) => !omit(key) && isActive(value)),
  );
}

/** The filters a write cannot change: equal ones make lists and aggregates siblings. */
export function boardOf<R>(rules: ViewRules<R>, filters: Filters): Filters {
  return normalized(filters, (key) => key in rules.accepts);
}

const same = (a: unknown, b: unknown) => hashKey([a]) === hashKey([b]);

/**
 * Whether a list that held the record proves it passes these filters' other
 * terms: it filtered by each of them the same way, and anything more it filtered
 * by only narrows.
 */
export function proven<R>(rules: ViewRules<R>, change: RecordChange<R>, filters: Filters) {
  const target = boardOf(rules, filters);
  return change.holders.some(
    (holder) =>
      Object.entries(target).every(([key, value]) => key in holder && same(holder[key], value)) &&
      Object.keys(holder).every((key) => key in target || !!rules.narrowing?.includes(key)),
  );
}

export function sameFilters(a: Filters, b: Filters): boolean {
  const none = () => false;
  return hashKey([normalized(a, none)]) === hashKey([normalized(b, none)]);
}

export function includes<R>(rules: ViewRules<R>, filters: Filters, row: R): boolean | undefined {
  let known = true;
  for (const [key, accepts] of Object.entries(rules.accepts)) {
    const verdict = accepts(filters[key], row);
    if (verdict === false) return false;
    if (verdict === undefined) known = false;
  }
  return known ? true : undefined;
}

export function opaqueChanged<R>(rules: ViewRules<R>, filters: Filters, before: R, after: R) {
  return Object.entries(rules.opaque ?? {}).some(
    ([key, read]) => isActive(filters[key]) && hashKey([read(before)]) !== hashKey([read(after)]),
  );
}

function rank(value: number | null, other: number | null, direction: SortTerm["direction"]) {
  if (value === other) return 0;
  if (value === null) return 1;
  if (other === null) return -1;
  return direction === "asc" ? value - other : other - value;
}

interface Placement<R> {
  same(a: R, b: R): boolean;
  index(loaded: readonly R[], row: R, complete: boolean): number | null;
}

/**
 * Where the backend's sort puts a row among the loaded ones, or null when a
 * term is not known to the client. A leading `runs` term (a text key the
 * backend keeps contiguous, such as a grouping) places the row inside the run
 * of its value; with no such run loaded its position is unknown.
 */
export function placementOf<R>(
  rules: ViewRules<R>,
  sort: readonly SortTerm[],
): Placement<R> | null {
  const [lead] = sort;
  const run = lead ? rules.runs?.[lead.key] : undefined;
  const terms = (run ? sort.slice(1) : sort).map((term) => ({
    read: rules.order?.[term.key],
    term,
  }));
  if (terms.some(({ read }) => read === undefined)) return null;
  const keys: Compare<R> = (a, b) => {
    for (const { read, term } of terms) {
      const order = read ? rank(read(a), read(b), term.direction) : 0;
      if (order !== 0) return order;
    }
    return 0;
  };
  const within: Compare<R> = (a, b) => keys(a, b) || rules.tieBreak(a, b);
  if (!run)
    return {
      same: (a, b) => keys(a, b) === 0,
      index: (loaded, row, complete) => {
        const at = loaded.findIndex((other) => within(row, other) < 0);
        if (at !== -1) return at;
        return complete ? loaded.length : null;
      },
    };
  const member = (a: R, b: R) => hashKey([run(a)]) === hashKey([run(b)]);
  return {
    same: (a, b) => member(a, b) && keys(a, b) === 0,
    index: (loaded, row, complete) => {
      const at = loaded.flatMap((other, index) => (member(other, row) ? [index] : []));
      const last = at.at(-1);
      if (last === undefined) return null;
      const before = at.find((index) => within(row, loaded[index] as R) < 0);
      if (before !== undefined) return before;
      return last + 1 === loaded.length && !complete ? null : last + 1;
    },
  };
}

export function byUuid<R>(
  direction: SortTerm["direction"],
  uuidOf: (row: R) => string,
): Compare<R> {
  return (a, b) => {
    const left = uuidOf(a).toLowerCase();
    const right = uuidOf(b).toLowerCase();
    if (left === right) return 0;
    return left < right === (direction === "asc") ? -1 : 1;
  };
}

const UNARCHIVED_STATUSES: readonly unknown[] = ["active", "inactive"];

/** Every unarchived status unless the filter asks for others. */
export const listedStatus =
  <R>(read: (row: R) => unknown) =>
  (value: unknown, row: R) =>
    (isActive(value) && Array.isArray(value) ? value : UNARCHIVED_STATUSES).includes(read(row));

/** At least one of the row's values among the filter's. */
export const overlaps =
  <R>(read: (row: R) => readonly unknown[]) =>
  (value: unknown, row: R) =>
    !isActive(value) || !Array.isArray(value) || read(row).some((item) => value.includes(item));

export const filled = (value: string | null | undefined) => (value ?? "").trim() !== "";

export const anyOf =
  <R>(read: (row: R) => unknown) =>
  (value: unknown, row: R) =>
    !isActive(value) || (Array.isArray(value) ? value : [value]).includes(read(row));

/**
 * Keeps the first of a row a later page repeats: a page fetched while an
 * optimistic removal is in flight starts one row early.
 */
export function distinctPages<P, R, TId>(shape: PageShape<P, R, TId>) {
  return <D extends { pages: P[] }>(data: D): D => {
    const seen = new Set<TId>();
    let changed = false;
    const pages = data.pages.map((page) => {
      const records = shape.records(page);
      const kept = records.filter((record) => {
        const id = shape.idOf(record);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      if (kept.length === records.length) return page;
      changed = true;
      return shape.withRecords(page, kept, 0);
    });
    return changed ? { ...data, pages } : data;
  };
}

type Groups<R> = R[][];

function insertAt<R>(groups: Groups<R>, index: number, row: R): Groups<R> {
  let offset = 0;
  return groups.map((group, at) => {
    const local = index - offset;
    offset += group.length;
    const fits = local >= 0 && (local < group.length || at === groups.length - 1);
    return fits && local <= group.length
      ? [...group.slice(0, local), row, ...group.slice(local)]
      : group;
  });
}

/**
 * Lists the backend filters and sorts: a change moves the record out of the
 * lists that stop accepting it and into the sorted position of those that start
 * to. A list that never held the record takes it only when a list with the same
 * non-patchable filters did, which proves it passes them. A position past the
 * loaded rows, while more exist, only moves the count.
 */
export function viewSlot<P, R, TId, TPatch>(def: {
  prefix: QueryKey;
  shape: ViewShape<P, R, TId>;
  merge: Merge<R, TPatch>;
  parse: (key: QueryKey) => ListView | null;
  rules: ViewRules<R>;
}): CacheSlot<TId, TPatch, R> {
  const { shape, rules } = def;

  const rebuilt = (pages: readonly P[], groups: Groups<R>, countDelta: number): P[] =>
    pages.map((page, at) => {
      const records = groups[at] ?? [];
      return countDelta === 0 && records === shape.records(page)
        ? page
        : shape.withRecords(page, records, countDelta);
    });

  const settle = (
    view: ListView,
    pages: readonly P[],
    id: TId,
    change: RecordChange<R>,
    patch: TPatch | null,
  ): P[] | null | undefined => {
    const before = change.before as R;
    const after = patch === null ? undefined : (change.after as R);
    const records = pages.map((page) => shape.records(page) as R[]);
    const current = records.flat().find((record) => shape.idOf(record) === id);
    const held = current !== undefined;
    if (!held && !proven(rules, change, view.filters)) return null;

    const was = held || includes(rules, view.filters, before);
    const is = after === undefined ? false : includes(rules, view.filters, after);
    if (was === undefined || is === undefined) return undefined;
    const wasIn = was;
    const isIn = is;
    const countDelta = Number(isIn) - Number(wasIn);
    const rest = held
      ? records.map((group) =>
          group.some((record) => shape.idOf(record) === id)
            ? group.filter((record) => shape.idOf(record) !== id)
            : group,
        )
      : records;
    if (!isIn || after === undefined)
      return held || countDelta !== 0 ? rebuilt(pages, rest, countDelta) : null;

    const row = held && patch !== null ? def.merge(current, patch) : after;
    const placement = placementOf(rules, view.sort);
    if (held && (placement === null || placement.same(current, row))) {
      return rebuilt(
        pages,
        records.map((group) => group.map((record) => (shape.idOf(record) === id ? row : record))),
        countDelta,
      );
    }
    const loaded = rest.flat();
    const [first] = pages;
    const complete = first !== undefined && loaded.length + Number(held) >= shape.total(first);
    const index = placement?.index(loaded, row, complete) ?? null;
    if (index !== null) return rebuilt(pages, insertAt(rest, index, row), countDelta);
    return held || countDelta !== 0 ? rebuilt(pages, rest, countDelta) : null;
  };

  const walk = (
    entries: readonly CacheEntry[],
    id: TId,
    change: RecordChange<R>,
    patch: TPatch | null,
  ): CacheEntry[] => {
    const changed: CacheEntry[] = [];
    for (const entry of entries) {
      const [key, data] = entry;
      const view = def.parse(key);
      const pages = pagesOf(data, shape.isPage);
      if (!pages || pages.length === 0) continue;
      const next =
        !view ||
        (patch !== null &&
          opaqueChanged(rules, view.filters, change.before as R, change.after as R))
          ? undefined
          : settle(view, pages, id, change, patch);
      if (next === undefined)
        changed.push(
          ...(patch === null
            ? removeFromPages(shape, [entry], id)
            : patchPages(shape, def.merge, [entry], id, patch)),
        );
      else if (next) changed.push([key, withPages(data, next)]);
    }
    return changed;
  };

  return {
    prefix: def.prefix,
    patchAll: (entries, id, patch, change) =>
      change.before === undefined || change.after === undefined
        ? patchPages(shape, def.merge, entries, id, patch)
        : walk(entries, id, change, patch),
    removeAll: (entries, id, change) =>
      change.before === undefined
        ? removeFromPages(shape, entries, id)
        : walk(entries, id, change, null),
    find: (entries, id) => findRecord(shape, entries, id),
    holders: (entries, id) =>
      entries.flatMap(([key, data]) => {
        const view = def.parse(key);
        const pages = pagesOf(data, shape.isPage);
        return view && pages && contains(shape, pages, id) ? [boardOf(rules, view.filters)] : [];
      }),
  };
}
